import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/utils/api'
import {
  Calendar, Clock, MapPin, Users, Car, Plus, Edit2, Trash2,
  X, Check, AlertCircle, ChevronRight, School, Heart,
  UserPlus, Church, Gamepad2, Home, CalendarCheck, Plane, PartyPopper, Briefcase
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format, addDays, startOfWeek, parseISO, isToday, isTomorrow } from 'date-fns'
import { useEventTypes } from '@/hooks/useEventTypes'

interface FamilyEvent {
  id: string
  title: string
  event_type: string
  description?: string
  location?: string
  event_date: string
  start_time?: string
  end_time?: string
  departure_time?: string
  all_day: boolean
  needs_ride: boolean
  affects_chores: boolean
  is_mandatory: boolean
  created_by: string
  created_by_name: string
  attendees: EventAttendee[]
}

interface EventAttendee {
  user_id: string
  display_name: string
  status: 'invited' | 'confirmed' | 'declined' | 'maybe'
  response_date?: string
  notes?: string
}

interface User {
  id: string
  display_name: string
  is_parent: boolean
}

const eventTypeIcons: Record<string, React.ReactNode> = {
  school_activity: <School className="w-4 h-4" />,
  sports: <Gamepad2 className="w-4 h-4" />,
  custody: <Heart className="w-4 h-4" />,
  work: <Briefcase className="w-4 h-4" />,
  appointment: <CalendarCheck className="w-4 h-4" />,
  celebration: <PartyPopper className="w-4 h-4" />,
  religious: <Church className="w-4 h-4" />,
  travel: <Plane className="w-4 h-4" />,
  fun_activity: <Gamepad2 className="w-4 h-4" />,
  other: <Calendar className="w-4 h-4" />,
}

const defaultIcon = <Calendar className="w-4 h-4" />

export const FamilyEvents: React.FC = () => {
  const { user } = useAuth()
  const allEventTypes = useEventTypes()
  const eventTypes = useMemo(() =>
    allEventTypes.family.map(t => ({
      ...t,
      icon: eventTypeIcons[t.value] || defaultIcon
    })),
    [allEventTypes]
  )
  const [events, setEvents] = useState<FamilyEvent[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState<FamilyEvent | null>(null)
  const [loading, setLoading] = useState(true)
  const [users, setUsers] = useState<User[]>([])
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list')
  const [selectedDate, setSelectedDate] = useState(new Date())

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    event_type: 'fun_activity',
    description: '',
    location: '',
    event_date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '',
    end_time: '',
    departure_time: '',
    all_day: false,
    needs_ride: false,
    affects_chores: false,
    is_mandatory: false,
    attendees: [] as { user_id: string; status: string }[]
  })

  useEffect(() => {
    loadEvents()
    loadUsers()
  }, [])

  const loadEvents = async () => {
    setLoading(true)
    try {
      const data = await apiClient.get('/api/events')
      setEvents(data || [])
    } catch (error: any) {
      console.warn('Family events endpoint not available:', error)
      // Gracefully handle missing endpoint - set empty array
      setEvents([])
      // Only show error toast if it's not a 404 or 502 (missing endpoint)
      if (error?.response?.status !== 404 && error?.response?.status !== 502) {
        toast.error('Failed to load family events')
      }
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {
    try {
      const data = await apiClient.get('/api/users')
      // Handle both array response and object with users array
      const users = Array.isArray(data) ? data : (data?.users || [])
      setUsers(users)
    } catch (error) {
      console.error('Failed to load users:', error)
      setUsers([]) // Set empty array on error
    }
  }

  const handleCreateEvent = async () => {
    if (!formData.title || !formData.event_date) {
      toast.error('Please fill in required fields')
      return
    }

    try {
      const eventData = {
        ...formData,
        invite_user_ids: formData.attendees.map(a => a.user_id)
      }
      delete (eventData as any).attendees

      await apiClient.post('/api/events', eventData)
      toast.success('Event created successfully!')
      setShowCreateModal(false)
      resetForm()
      loadEvents()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create event')
    }
  }

  const handleUpdateEvent = async () => {
    if (!editingEvent) return

    try {
      const eventData = {
        ...formData,
        invite_user_ids: formData.attendees.map(a => a.user_id)
      }
      delete (eventData as any).attendees

      await apiClient.put(`/api/events/${editingEvent.id}`, eventData)
      toast.success('Event updated successfully!')
      setEditingEvent(null)
      resetForm()
      loadEvents()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update event')
    }
  }

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm('Are you sure you want to delete this event?')) return

    try {
      await apiClient.delete(`/api/events/${eventId}`)
      toast.success('Event deleted successfully')
      loadEvents()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete event')
    }
  }

  const handleRespondToEvent = async (eventId: string, status: string) => {
    try {
      await apiClient.post(`/api/events/${eventId}/respond`, { status })
      toast.success(`Response recorded: ${status}`)
      loadEvents()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to respond to event')
    }
  }

  const toggleAttendee = (userId: string) => {
    const existing = formData.attendees.find(a => a.user_id === userId)
    if (existing) {
      setFormData({
        ...formData,
        attendees: formData.attendees.filter(a => a.user_id !== userId)
      })
    } else {
      setFormData({
        ...formData,
        attendees: [...formData.attendees, { user_id: userId, status: 'invited' }]
      })
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      event_type: 'fun_activity',
      description: '',
      location: '',
      event_date: format(new Date(), 'yyyy-MM-dd'),
      start_time: '',
      end_time: '',
      departure_time: '',
      all_day: false,
      needs_ride: false,
      affects_chores: false,
      is_mandatory: false,
      attendees: []
    })
  }

  const openEditModal = (event: FamilyEvent) => {
    setEditingEvent(event)
    setFormData({
      title: event.title,
      event_type: event.event_type,
      description: event.description || '',
      location: event.location || '',
      event_date: event.event_date,
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      departure_time: event.departure_time || '',
      all_day: event.all_day,
      needs_ride: event.needs_ride,
      affects_chores: event.affects_chores,
      is_mandatory: event.is_mandatory,
      attendees: (event.attendees || []).map(a => ({
        user_id: a.user_id,
        status: a.status
      }))
    })
    setShowCreateModal(true)
  }

  const getEventTypeInfo = (type: string) => {
    return eventTypes.find(t => t.value === type) || eventTypes[eventTypes.length - 1]
  }

  const formatEventTime = (event: FamilyEvent) => {
    if (event.all_day) return 'All Day'

    // Handle case where neither time exists
    if (!event.start_time && !event.end_time) return ''

    // Parse time strings (format: HH:MM:SS or HH:MM:SS.ffffff)
    const formatTime = (timeStr: string | null) => {
      if (!timeStr) return null
      const [hours, minutes] = timeStr.split(':')
      const hour = parseInt(hours, 10)
      const min = minutes || '00'
      const ampm = hour >= 12 ? 'PM' : 'AM'
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      return `${displayHour}:${min} ${ampm}`
    }

    const start = formatTime(event.start_time)
    const end = formatTime(event.end_time)

    // Handle different combinations of start/end times
    if (start && end) {
      return `${start} - ${end}`
    } else if (start) {
      return `Starts ${start}`
    } else if (end) {
      return `Ends ${end}`
    }

    return ''
  }

  const getEventDateLabel = (dateStr: string) => {
    const date = parseISO(dateStr)
    if (isToday(date)) return 'Today'
    if (isTomorrow(date)) return 'Tomorrow'
    return format(date, 'MMM d')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-500" />
            Family Events & Activities
          </h2>
          <p className="text-gray-600 mt-1">Manage family events and respond to kid requests</p>
        </div>
        {user?.is_parent && (
          <button
            onClick={() => {
              resetForm()
              setEditingEvent(null)
              setShowCreateModal(true)
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Event
          </button>
        )}
      </div>

      {/* Quick Create Buttons */}
      {user?.is_parent && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => {
              resetForm()
              setFormData(prev => ({
                ...prev,
                title: 'Mass',
                event_type: 'religious',
                location: 'Church',
                start_time: '12:15',
                end_time: '13:15',
                departure_time: '12:00',
                is_mandatory: true
              }))
              setShowCreateModal(true)
            }}
            className="p-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 flex flex-col items-center gap-1"
          >
            <Church className="w-5 h-5" />
            <span className="text-sm font-medium">Religious</span>
          </button>

          <button
            onClick={() => {
              resetForm()
              setFormData(prev => ({
                ...prev,
                title: 'Fun Activity',
                event_type: 'fun_activity',
                location: "Andretti's Karting"
              }))
              setShowCreateModal(true)
            }}
            className="p-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 flex flex-col items-center gap-1"
          >
            <Gamepad2 className="w-5 h-5" />
            <span className="text-sm font-medium">Fun Activity</span>
          </button>

          <button
            onClick={() => {
              resetForm()
              setFormData(prev => ({
                ...prev,
                title: 'School Event',
                event_type: 'school_activity'
              }))
              setShowCreateModal(true)
            }}
            className="p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 flex flex-col items-center gap-1"
          >
            <School className="w-5 h-5" />
            <span className="text-sm font-medium">School</span>
          </button>

          <button
            onClick={() => {
              resetForm()
              setFormData(prev => ({
                ...prev,
                title: 'Appointment',
                event_type: 'appointment'
              }))
              setShowCreateModal(true)
            }}
            className="p-3 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 flex flex-col items-center gap-1"
          >
            <CalendarCheck className="w-5 h-5" />
            <span className="text-sm font-medium">Appointment</span>
          </button>
        </div>
      )}

      {/* Events List */}
      <div className="space-y-4">
        {events.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No upcoming events</p>
            {user?.is_parent && (
              <p className="text-sm text-gray-500 mt-1">Create your first family event to get started</p>
            )}
          </div>
        ) : (
          events
            .filter(event => {
              // Filter to show only recent past (7 days) and future events
              const eventDate = new Date(event.event_date + 'T12:00:00')
              const today = new Date()
              today.setHours(0, 0, 0, 0) // Start of today
              const sevenDaysAgo = new Date(today)
              sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
              return eventDate >= sevenDaysAgo
            })
            .sort((a, b) => new Date(a.event_date + 'T12:00:00').getTime() - new Date(b.event_date + 'T12:00:00').getTime())
            .map(event => {
            const typeInfo = getEventTypeInfo(event.event_type)
            const myAttendance = event.attendees?.find(a => a.user_id === user?.id)
            
            return (
              <div key={event.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg bg-blue-50 text-blue-600`}>
                          {typeInfo.icon}
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900">{event.title}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {getEventDateLabel(event.event_date)}
                            </span>
                            {(event.start_time || event.all_day) && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatEventTime(event)}
                              </span>
                            )}
                            {event.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {event.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {event.description && (
                        <p className="text-gray-600 mb-3">{event.description}</p>
                      )}

                      {/* Event Details */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {event.departure_time && (
                          <span className="px-2 py-1 bg-yellow-50 text-yellow-700 text-xs rounded-full flex items-center gap-1">
                            <Car className="w-3 h-3" />
                            Leave at {(() => {
                              try {
                                // Parse time string (HH:MM:SS or HH:MM:SS.SSSSSS)
                                const timeMatch = event.departure_time.match(/^(\d{1,2}):(\d{2})/)
                                if (timeMatch) {
                                  const hours = parseInt(timeMatch[1])
                                  const minutes = timeMatch[2]
                                  const ampm = hours >= 12 ? 'PM' : 'AM'
                                  const displayHours = hours % 12 || 12
                                  return `${displayHours}:${minutes} ${ampm}`
                                }
                                return event.departure_time
                              } catch {
                                return event.departure_time
                              }
                            })()}
                          </span>
                        )}
                        {event.needs_ride && (
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                            Needs ride
                          </span>
                        )}
                        {event.affects_chores && (
                          <span className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded-full">
                            Affects chores
                          </span>
                        )}
                        {event.is_mandatory && (
                          <span className="px-2 py-1 bg-red-50 text-red-700 text-xs rounded-full">
                            Mandatory
                          </span>
                        )}
                      </div>

                      {/* Attendees */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Who's going:</span>
                        <div className="flex flex-wrap gap-2">
                          {(event.attendees || []).map(attendee => (
                            <span
                              key={attendee.user_id}
                              className={`px-2 py-1 text-xs rounded-full ${
                                attendee.status === 'confirmed'
                                  ? 'bg-green-100 text-green-700'
                                  : attendee.status === 'declined'
                                  ? 'bg-red-100 text-red-700'
                                  : attendee.status === 'maybe'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {attendee.display_name}
                              {attendee.status === 'confirmed' && ' ✓'}
                              {attendee.status === 'declined' && ' ✗'}
                              {attendee.status === 'maybe' && ' ?'}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 ml-4">
                      {user?.is_parent ? (
                        <>
                          <button
                            onClick={() => openEditModal(event)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEvent(event.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      ) : myAttendance && (
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleRespondToEvent(event.id, 'confirmed')}
                            className={`px-3 py-1 text-xs rounded ${
                              myAttendance.status === 'confirmed'
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-green-100'
                            }`}
                          >
                            Going
                          </button>
                          <button
                            onClick={() => handleRespondToEvent(event.id, 'maybe')}
                            className={`px-3 py-1 text-xs rounded ${
                              myAttendance.status === 'maybe'
                                ? 'bg-yellow-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-yellow-100'
                            }`}
                          >
                            Maybe
                          </button>
                          <button
                            onClick={() => handleRespondToEvent(event.id, 'declined')}
                            className={`px-3 py-1 text-xs rounded ${
                              myAttendance.status === 'declined'
                                ? 'bg-red-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-red-100'
                            }`}
                          >
                            Can't Go
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold">
                  {editingEvent ? 'Edit Event' : 'Create Family Event'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateModal(false)
                    setEditingEvent(null)
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Event Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event Type
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {eventTypes.map(type => (
                      <button
                        key={type.value}
                        onClick={() => setFormData({ ...formData, event_type: type.value })}
                        className={`p-2 rounded-lg border ${
                          formData.event_type === type.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          {type.icon}
                          <span className="text-xs">{type.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event Name *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Mass, Andretti's Karting, Doctor Appointment"
                  />
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={formData.event_date}
                      onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <input
                        type="checkbox"
                        checked={formData.all_day}
                        onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
                        className="mr-2"
                      />
                      All Day Event
                    </label>
                  </div>
                </div>

                {!formData.all_day && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Leave House At
                      </label>
                      <input
                        type="time"
                        value={formData.departure_time}
                        onChange={(e) => setFormData({ ...formData, departure_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                )}

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Church, Andretti's Karting, School"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                    placeholder="Any additional details..."
                  />
                </div>

                {/* Attendees */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Who's Going?
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {users.map(user => (
                      <label
                        key={user.id}
                        className="flex items-center gap-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.attendees.some(a => a.user_id === user.id)}
                          onChange={() => toggleAttendee(user.id)}
                        />
                        <span>{user.display_name}</span>
                        {user.is_parent && (
                          <span className="text-xs text-gray-500">(Parent)</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.needs_ride}
                      onChange={(e) => setFormData({ ...formData, needs_ride: e.target.checked })}
                    />
                    <span className="text-sm">Needs ride arrangements</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.affects_chores}
                      onChange={(e) => setFormData({ ...formData, affects_chores: e.target.checked })}
                    />
                    <span className="text-sm">Affects chore assignments</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_mandatory}
                      onChange={(e) => setFormData({ ...formData, is_mandatory: e.target.checked })}
                    />
                    <span className="text-sm">Mandatory attendance</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false)
                    setEditingEvent(null)
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={editingEvent ? handleUpdateEvent : handleCreateEvent}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  {editingEvent ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}